'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const cardVariants = cva(
  'rounded-xl border transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'border-[var(--color-border)] bg-[var(--color-surface)]',
        glass: 'border-[var(--color-border)] bg-[var(--color-surface)]/80 backdrop-blur-sm',
        metric: 'border-[var(--color-border)] bg-[var(--color-surface)] border-l-4',
      },
      padding: {
        none: '',
        sm: 'p-3',
        md: 'p-5',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  }
)

const accentColorMap = {
  lime: 'border-l-[var(--color-lime)]',
  pink: 'border-l-[var(--color-pink)]',
  cyan: 'border-l-[var(--color-cyan)]',
  purple: 'border-l-[var(--color-purple)]',
  orange: 'border-l-[var(--color-orange)]',
  green: 'border-l-[var(--color-green)]',
  blue: 'border-l-[var(--color-blue)]',
} as const

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  accentColor?: keyof typeof accentColorMap
  hoverable?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, accentColor, hoverable = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({ variant, padding }),
          hoverable && 'hover:border-[var(--color-border-hover)] hover:shadow-lg',
          variant === 'metric' && accentColor && accentColorMap[accentColor],
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('pb-4 mb-4 border-b border-[var(--color-border)]', className)}
      {...props}
    />
  )
)
CardHeader.displayName = 'CardHeader'

const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('', className)} {...props} />
  )
)
CardBody.displayName = 'CardBody'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('pt-4 mt-4 border-t border-[var(--color-border)]', className)}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'

const CardCompound = Object.assign(Card, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
})

export { CardCompound as Card }
